import { useCallback } from 'react';
import { Input } from '@/components/ui/Input';
import { Button } from '@/components/ui/Button';
import { getIconDef, getServicesByPrefix } from '@/lib/icons';
import { IconRenderer } from '@/components/ui/IconRenderer';
import { EDGE_STYLES } from '@/lib/constants';
import { useEditorStore } from '../store';
import { useBlastRadius } from '../lib/blastRadiusContext';
import type { EdgeStyle } from '@/types/diagram';

const allServices = Object.entries(getServicesByPrefix()).flatMap(([, items]) => items);

export function NodeProperties() {
  const selectedNodeId = useEditorStore((s) => s.selectedNodeId);
  const selectedEdgeId = useEditorStore((s) => s.selectedEdgeId);
  const nodes = useEditorStore((s) => s.nodes);
  const edges = useEditorStore((s) => s.edges);
  const updateNodeData = useEditorStore((s) => s.updateNodeData);
  const removeNode = useEditorStore((s) => s.removeNode);
  const duplicateNode = useEditorStore((s) => s.duplicateNode);
  const updateEdgeLabel = useEditorStore((s) => s.updateEdgeLabel);
  const updateEdgeStyle = useEditorStore((s) => s.updateEdgeStyle);
  const removeEdge = useEditorStore((s) => s.removeEdge);

  const node = selectedNodeId ? nodes.find((n) => n.id === selectedNodeId) : null;
  const edge = selectedEdgeId ? edges.find((e) => e.id === selectedEdgeId) : null;

  if (edge) {
    return (
      <EdgeProperties
        edge={edge}
        onLabelChange={(label) => updateEdgeLabel(edge.id, label)}
        onStyleChange={(style) => updateEdgeStyle(edge.id, style)}
        onDelete={() => removeEdge(edge.id)}
      />
    );
  }

  if (!node || !selectedNodeId) {
    return (
      <div className="p-4">
        <p className="text-petit text-ink-muted">
          Select a node or edge to edit its properties
        </p>
        <div className="mt-4 space-y-1 text-micro text-ink-faint">
          <p>Ctrl+Z — Undo</p>
          <p>Ctrl+Shift+Z — Redo</p>
          <p>Ctrl+D — Duplicate</p>
          <p>Delete — Remove selected</p>
        </div>
      </div>
    );
  }

  return (
    <NodePanel
      node={node}
      onLabelChange={(label) => updateNodeData(selectedNodeId, { label })}
      onTypeChange={(nodeType) => updateNodeData(selectedNodeId, { nodeType })}
      onDuplicate={() => duplicateNode(selectedNodeId)}
      onDelete={() => removeNode(selectedNodeId)}
    />
  );
}

// BlastRadiusControls is a single-purpose block: trigger blast-radius
// mode for the selected node, show the affected count, and let the user
// exit. Lives inline in the properties panel so the discovery path is
// "select node → see button → click → diagram lights up red."
function BlastRadiusControls({ nodeId }: { nodeId: string }) {
  const setBlastRadiusFocus = useEditorStore((s) => s.setBlastRadiusFocus);
  const { focus, affected, spofs } = useBlastRadius();
  const isActive = focus === nodeId;
  const isSPOF = spofs.has(nodeId);

  // affected.size includes the focused node itself; subtract so the
  // count means "downstream things broken", which is what users expect.
  const downstreamCount = isActive ? Math.max(0, affected.size - 1) : 0;

  return (
    <div className="space-y-1.5">
      <label className="block text-petit font-medium text-ink-secondary">
        Blast radius
        {isSPOF && (
          <span
            className="ml-2 inline-flex items-center gap-1 rounded bg-amber-500/15 px-1.5 py-0.5 text-micro font-medium text-amber-300 [body.paper_&]:bg-amber-100 [body.paper_&]:text-amber-700"
            title="Failure cascades to >25% of nodes"
          >
            SPOF
          </span>
        )}
      </label>
      {!isActive ? (
        <Button
          variant="secondary"
          size="sm"
          className="w-full"
          onClick={() => setBlastRadiusFocus(nodeId)}
        >
          What breaks if this fails?
        </Button>
      ) : (
        <>
          <p className="text-petit text-ink-secondary">
            <span className="font-semibold text-danger">{downstreamCount}</span>{' '}
            downstream {downstreamCount === 1 ? 'node breaks' : 'nodes break'} if this fails.
          </p>
          <Button
            variant="secondary"
            size="sm"
            className="w-full"
            onClick={() => setBlastRadiusFocus(null)}
          >
            Exit blast view
          </Button>
        </>
      )}
    </div>
  );
}

// --- Node panel ---

interface NodePanelProps {
  node: { id: string; type?: string; position: { x: number; y: number }; data: Record<string, unknown> };
  onLabelChange: (label: string) => void;
  onTypeChange: (type: string) => void;
  onDuplicate: () => void;
  onDelete: () => void;
}

function NodePanel({ node, onLabelChange, onTypeChange, onDuplicate, onDelete }: NodePanelProps) {
  const label = (node.data.label as string) ?? '';
  const nodeType = (node.data.nodeType as string) ?? 'generic.server';
  const icon = getIconDef(nodeType);
  const isGroup = node.type === 'group';

  const handleLabel = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => onLabelChange(e.target.value),
    [onLabelChange],
  );
  const handleType = useCallback(
    (e: React.ChangeEvent<HTMLSelectElement>) => onTypeChange(e.target.value),
    [onTypeChange],
  );

  return (
    <div className="flex flex-col gap-4 p-3">
      <div className="flex items-center gap-2">
        <span
          className="flex h-8 w-8 items-center justify-center rounded-clyra"
          style={{ backgroundColor: `${icon.color}12`, color: icon.color }}
        >
          <IconRenderer name={icon.icon} size={18} strokeWidth={1.5} />
        </span>
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-medium text-ink-primary">{label}</p>
          <p className="text-micro text-ink-faint">{node.id}</p>
        </div>
      </div>

      <hr className="border-line-subtle" />

      <Input label="Label" value={label} onChange={handleLabel} />

      {!isGroup && (
        <div className="space-y-1.5">
          <label className="block text-petit font-medium text-ink-secondary">Service type</label>
          <select
            value={nodeType}
            onChange={handleType}
            className="w-full rounded-clyra border border-line-default bg-clyra-bg px-3 py-2 text-sm text-ink-primary outline-none focus:border-brand-500/50 focus:ring-1 focus:ring-brand-500/30 transition-colors"
          >
            {allServices.map((s) => (
              <option key={s.type} value={s.type}>
                {s.icon.label} — {s.type}
              </option>
            ))}
          </select>
        </div>
      )}

      <div className="space-y-1.5">
        <label className="block text-petit font-medium text-ink-secondary">Position</label>
        <div className="flex gap-2 text-petit text-ink-muted font-mono">
          <span>x: {Math.round(node.position.x)}</span>
          <span>y: {Math.round(node.position.y)}</span>
        </div>
      </div>

      {!isGroup && (
        <>
          <hr className="border-line-subtle" />
          <BlastRadiusControls nodeId={node.id} />
        </>
      )}

      <hr className="border-line-subtle" />

      <div className="flex gap-2">
        {!isGroup && (
          <Button variant="secondary" size="sm" className="flex-1" onClick={onDuplicate}>
            Duplicate
          </Button>
        )}
        <Button variant="danger" size="sm" className="flex-1" onClick={onDelete}>
          Delete
        </Button>
      </div>
    </div>
  );
}

// --- Edge panel ---

interface EdgePropertiesProps {
  edge: { id: string; label?: string | React.ReactNode; style?: React.CSSProperties; animated?: boolean };
  onLabelChange: (label: string) => void;
  onStyleChange: (style: EdgeStyle) => void;
  onDelete: () => void;
}

function EdgeProperties({ edge, onLabelChange, onStyleChange, onDelete }: EdgePropertiesProps) {
  const currentLabel = typeof edge.label === 'string' ? edge.label : '';

  const currentStyle: EdgeStyle = (() => {
    const dash = edge.style?.strokeDasharray;
    if (typeof dash === 'string') {
      if (dash.startsWith('2')) return 'dotted';
      if (dash.startsWith('6')) return 'dashed';
    }
    if (edge.animated) return 'dashed';
    return 'solid';
  })();

  return (
    <div className="flex flex-col gap-4 p-3">
      <div>
        <p className="text-sm font-medium text-ink-primary">Edge</p>
        <p className="text-micro text-ink-faint">{edge.id}</p>
      </div>

      <hr className="border-line-subtle" />

      <Input
        label="Label"
        value={currentLabel}
        onChange={(e) => onLabelChange(e.target.value)}
        placeholder="e.g. HTTPS, gRPC..."
      />

      <div className="space-y-1.5">
        <label className="block text-petit font-medium text-ink-secondary">Line style</label>
        <div className="flex gap-1">
          {EDGE_STYLES.map((style) => (
            <button
              key={style}
              onClick={() => onStyleChange(style)}
              className={`flex-1 rounded-clyra border px-2 py-1.5 text-petit capitalize transition-colors ${
                currentStyle === style
                  ? 'border-brand-500/40 bg-brand-500/8 text-brand-400'
                  : 'border-line-default text-ink-muted hover:border-line-strong'
              }`}
            >
              {style}
            </button>
          ))}
        </div>
      </div>

      <hr className="border-line-subtle" />

      <Button variant="danger" size="sm" onClick={onDelete}>
        Delete edge
      </Button>
    </div>
  );
}
